import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

const SAMPLE_ADDRESSES = [
  "123 Bayshore Blvd, Tampa, FL 33606",
  "456 Central Ave, St. Petersburg, FL 33701",
  "789 Cortez Rd W, Bradenton, FL 34210",
  "1010 Main St, Sarasota, FL 34236",
  "2020 Venice Ave, Venice, FL 34285",
  "3030 Tamiami Trl, North Port, FL 34287",
  "4040 Tamiami Trl, Port Charlotte, FL 33952",
  "5050 US Hwy 41, Punta Gorda, FL 33950",
  "6060 Cleveland Ave, Fort Myers, FL 33901",
  "7070 S Florida Ave, Lakeland, FL 33803",
  "8080 Moccasin Wallow Rd, Parrish, FL 34219",
  "9090 Gulf Blvd, Clearwater, FL 33767",
  "1234 W Swann Ave, Tampa, FL 33606",
  "5678 Gulf Blvd, St Pete Beach, FL 33706",
  "3456 Bee Ridge Rd, Sarasota, FL 34233",
  "7890 Colonial Blvd, Fort Myers, FL 33966",
  "2345 Lakeland Hills Blvd, Lakeland, FL 33805",
  "4567 34th St S, St. Petersburg, FL 33711",
  "6789 Clark Rd, Sarasota, FL 34241",
  "8901 Daniels Pkwy, Fort Myers, FL 33912",
  "1357 Dale Mabry Hwy, Tampa, FL 33609",
  "2468 McGregor Blvd, Fort Myers, FL 33901",
  "3579 Fruitville Rd, Sarasota, FL 34237",
  "4680 US Hwy 19 N, Clearwater, FL 33764",
];

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function AddressAutocomplete({ value, onChange, placeholder = "Start typing an address…", className, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (v: string) => {
    onChange(v);
    if (v.length >= 1) {
      const q = v.toLowerCase().trim();
      // Smart matching: match from start of house number, street name, or city
      const matches = SAMPLE_ADDRESSES.filter((a) => {
        const lower = a.toLowerCase();
        // Match if query appears at start of the address
        if (lower.startsWith(q)) return true;
        // Match any word boundary
        const words = lower.split(/[\s,]+/);
        return words.some(w => w.startsWith(q)) || lower.includes(q);
      }).slice(0, 6);
      setSuggestions(matches);
      setOpen(matches.length > 0);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const selectAddress = (addr: string) => {
    onChange(addr);
    setOpen(false);
  };

  // Highlight matching text
  const highlightMatch = (addr: string) => {
    if (!value.trim()) return addr;
    const idx = addr.toLowerCase().indexOf(value.toLowerCase().trim());
    if (idx === -1) return addr;
    return (
      <>
        {addr.slice(0, idx)}
        <span className="font-bold text-foreground">{addr.slice(idx, idx + value.trim().length)}</span>
        {addr.slice(idx + value.trim().length)}
      </>
    );
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className={`pl-8 ${className || ""}`}
          disabled={disabled}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          autoComplete="off"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((addr) => (
            <button
              key={addr}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors flex items-center gap-2"
              onClick={() => selectAddress(addr)}
            >
              <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
              <span>{highlightMatch(addr)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
